import { useState, useEffect, useRef } from 'react';
import {
    Bell,
    MessageCircle,
    Laptop,
    LogOut,
    Sun,
    Moon,
    Monitor,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import NotifikaceDropdown from './NotifikaceDropdown';
import ActivityBar from './sidebar/ActivityBar';
import SubMenu from './sidebar/SubMenu';
import SearchPalette from './sidebar/SearchPalette';
import { MAX_IKON_MODULU, viditelneSekce } from '../lib/moduly';
import { MODAL_SKLO } from '../lib/uiTokens';
import ModalShell from './ui/ModalShell';
import PrihlasenaZarizeni from './profil/PrihlasenaZarizeni';

const THEMES = [
    { v: 'light',  Icon: Sun,     label: 'Světlý' },
    { v: 'dark',   Icon: Moon,    label: 'Tmavý'  },
    { v: 'system', Icon: Monitor, label: 'Systém' },
];

// Prodleva, než se panel sekce otevře po najetí (ať neblikají panely při
// přejezdu myší přes lištu) a než se zavře po odjetí (ať jde přejet mezerou
// mezi lištou a panelem).
const OTEVRIT_PO_MS = 200;
const ZAVRIT_PO_MS = 250;

// ── Levá lišta (desktop) ───────────────────────────────────────
// Katalog modulů a sekcí je v lib/moduly(.json). Mobil má vlastní spodní
// lištu a MobilniMenu v app.jsx — tady se na mobilu nic nevykresluje.
export default function Sidebar({ currentPage, onNavigate, user, onLogout, onUserUpdate, isMobile, pocetNotifikaci = 0 }) {
    const { theme, setTheme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);


    // Co uživatel vidí. Do MAX_IKON_MODULU modulů rovnou ikony modulů, nad to
    // ikony sekcí s panelem — rozhoduje počet, ne role.
    const sekce = viditelneSekce(user);
    const pocetModulu = sekce.reduce((n, s) => n + s.items.length, 0);
    const rezimSekci = pocetModulu > MAX_IKON_MODULU;

    // ── Počty u ikon ────────────────────────────────────────
    // Nepřečtené notifikace drží app.jsx (realtime + polling) a posílá je sem
    const unreadCount = pocetNotifikaci;
    const [chatUnreadCount, setChatUnreadCount] = useState(0);

    useEffect(() => {
        fetch('/api/chat/neprectenych').then(r => r.ok ? r.json() : { count: 0 }).then(d => setChatUnreadCount(d.count || 0)).catch(() => {});
        const handleChatBadge = (e) => setChatUnreadCount(e.detail?.count ?? 0);
        window.addEventListener('chat-badge-update', handleChatBadge);
        return () => window.removeEventListener('chat-badge-update', handleChatBadge);
    }, []);

    // ── Panel sekce ─────────────────────────────────────────
    // anchorTop = Y ikony sekce ve viewportu, panel se k ní kotví.
    const [openSection, setOpenSection] = useState(null);
    const [anchorTop, setAnchorTop] = useState(0);
    const timerRef = useRef(null);

    const zrusCasovac = () => {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };

    // Najetí na ikonu sekce otevře její panel; `null` (ikona, která není
    // sekce) otevřený panel zavře.
    const handleSectionHover = (sectionId, rect) => {
        zrusCasovac();
        timerRef.current = setTimeout(() => {
            setOpenSection(sectionId);
            if (rect) setAnchorTop(rect.top);
        }, sectionId ? OTEVRIT_PO_MS : ZAVRIT_PO_MS);
    };

    const handleSectionClick = (sectionId, rect) => {
        zrusCasovac();
        setOpenSection((otevrena) => (otevrena === sectionId ? null : sectionId));
        if (rect) setAnchorTop(rect.top);
    };

    const zavriPozdeji = () => {
        zrusCasovac();
        timerRef.current = setTimeout(() => setOpenSection(null), ZAVRIT_PO_MS);
    };

    const zavriPanel = () => {
        zrusCasovac();
        setOpenSection(null);
    };

    useEffect(() => zrusCasovac, []);

    useEffect(() => {
        if (!openSection) return;
        const handler = (e) => {
            if (!e.target.closest?.('.sidebar-submenu') && !e.target.closest?.('.sidebar-activity-bar')) {
                setOpenSection(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [openSection]);

    const otevrenaSekce = openSection ? sekce.find((s) => s.id === openSection) : null;

    // ── Hledání (Ctrl/Cmd+K) — pro každého, kdo má sekce ────
    const [searchOpen, setSearchOpen] = useState(false);

    useEffect(() => {
        if (!rezimSekci) return;
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                setSearchOpen((v) => !v);
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [rezimSekci]);

    // Nedávné z hledání se ukládají do sidebar_state (debounce 500 ms).
    const persistTimerRef = useRef(null);
    const handleUpdateSearchRecent = (newRecent) => {
        if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
        persistTimerRef.current = setTimeout(() => {
            const token = document.querySelector('meta[name="csrf-token"]')?.content;
            fetch('/auth/sidebar-state', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token, 'Accept': 'application/json' },
                body: JSON.stringify({ search_recent: newRecent }),
            }).catch(() => {});
        }, 500);
        onUserUpdate?.({
            ...user,
            sidebar_state: { ...(user?.sidebar_state || {}), search_recent: newRecent },
        });
    };

    // ── Spodní dock: notifikace, zprávy, profil ─────────────
    const [profileOpen, setProfileOpen] = useState(false);
    const [zarizeniOpen, setZarizeniOpen] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const [avatarError, setAvatarError] = useState(false);
    const profileRef = useRef(null);
    const profilePanelRef = useRef(null);
    const notifRef = useRef(null);

    const logoSrc = isDark ? '/images/logo-white.png' : '/images/logo-dark.png';

    // Klik mimo zavře profil — kontroluje i panel, jinak by mousedown na
    // položce uvnitř zavřel panel dřív, než odpálí onClick.
    useEffect(() => {
        if (!profileOpen) return;
        const handler = (e) => {
            const inTrigger = profileRef.current?.contains(e.target);
            const inPanel = profilePanelRef.current?.contains(e.target);
            if (!inTrigger && !inPanel) setProfileOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [profileOpen]);

    if (isMobile) {
        return null;
    }

    const dockBtnStyle = (active, accent = '#FF8A00') => ({
        position: 'relative',
        width: 40, height: 36, borderRadius: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: 'none', cursor: 'pointer',
        background: active ? `${accent}18` : 'transparent',
        color: active ? accent : (isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)'),
        transition: 'all 0.15s ease',
    });

    const DockBadge = ({ count, color = '#FF8A00' }) => count > 0 ? (
        <span style={{
            position: 'absolute', top: 4, right: 4,
            minWidth: 14, height: 14, padding: '0 3px',
            background: color, color: '#fff',
            fontSize: 9, fontWeight: 700, borderRadius: 5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1, border: '1.5px solid var(--surface-sidebar)',
        }}>
            {count > 9 ? '9+' : count}
        </span>
    ) : null;

    const oddelovac = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)';

    return (
        <>
            <aside
                style={{
                    width: 60,
                    background: 'var(--surface-sidebar)',
                    backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                    borderRight: '1px solid var(--border-default)',
                    boxShadow: 'var(--shadow-sidebar)',
                    position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 40,
                }}
                className="flex flex-col"
            >
                <div style={{ padding: '10px 4px 8px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                    <img src={logoSrc} alt="FofrLama" style={{ width: 42, height: 42, objectFit: 'contain' }} />
                </div>

                <div style={{ height: 1, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', margin: '0 6px', flexShrink: 0 }} />

                <ActivityBar
                    user={user}
                    currentPage={currentPage}
                    onNavigate={onNavigate}
                    sekce={sekce}
                    rezimSekci={rezimSekci}
                    openSection={openSection}
                    onSectionHover={handleSectionHover}
                    onSectionClick={handleSectionClick}
                    onMouseLeave={rezimSekci ? zavriPozdeji : undefined}
                    onSearchClick={rezimSekci ? () => { zavriPanel(); setSearchOpen(true); } : undefined}
                    isDark={isDark}
                />

                <div style={{ height: 1, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', margin: '0 6px', flexShrink: 0 }} />

                <div style={{ padding: '8px 0 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <button
                        ref={notifRef}
                        onClick={() => { setNotifOpen(v => !v); setProfileOpen(false); }}
                        title="Notifikace"
                        style={dockBtnStyle(notifOpen || currentPage === 'aktivita')}
                        onMouseEnter={e => { if (!notifOpen && currentPage !== 'aktivita') e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)'; }}
                        onMouseLeave={e => { if (!notifOpen && currentPage !== 'aktivita') e.currentTarget.style.background = 'transparent'; }}
                    >
                        <Bell size={20} strokeWidth={(notifOpen || currentPage === 'aktivita') ? 2.2 : 1.8} />
                        <DockBadge count={unreadCount} />
                    </button>

                    <button
                        onClick={() => window.dispatchEvent(new Event('minichat-toggle'))}
                        title="Zprávy"
                        style={dockBtnStyle(currentPage === 'zpravy')}
                        onMouseEnter={e => { if (currentPage !== 'zpravy') e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)'; }}
                        onMouseLeave={e => { if (currentPage !== 'zpravy') e.currentTarget.style.background = 'transparent'; }}
                    >
                        <MessageCircle size={20} strokeWidth={currentPage === 'zpravy' ? 2.2 : 1.8} />
                        <DockBadge count={chatUnreadCount} />
                    </button>

                    <div ref={profileRef} style={{ position: 'relative' }}>
                        <button
                            onClick={() => { setProfileOpen(v => !v); setNotifOpen(false); }}
                            title={user?.cele_jmeno || 'Profil'}
                            style={{
                                position: 'relative',
                                width: 46, height: 46, borderRadius: '50%',
                                border: 'none', cursor: 'pointer', padding: 0,
                                background: profileOpen
                                    ? (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)')
                                    : 'transparent',
                                transition: 'all 0.15s ease',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                            onMouseEnter={e => { if (!profileOpen) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)'; }}
                            onMouseLeave={e => { if (!profileOpen) e.currentTarget.style.background = 'transparent'; }}
                        >
                            {user?.avatar_url && !avatarError
                                ? <img src={user.avatar_url} className="rounded-full object-cover" style={{ width: 42, height: 42, objectPosition: user?.avatar_position || 'center' }} alt="" onError={() => setAvatarError(true)} />
                                : <div className="rounded-full flex items-center justify-center text-sm font-bold" style={{ width: 42, height: 42, background: 'var(--brand-bg)', color: 'var(--brand-text)' }}>{user?.jmeno?.[0]}{user?.prijmeni?.[0]}</div>
                            }
                            <span className="absolute rounded-full" style={{ bottom: 3, right: 3, width: 11, height: 11, background: '#22C55E', border: '2px solid var(--surface-sidebar)' }} />
                        </button>
                    </div>
                </div>
            </aside>

            {rezimSekci && otevrenaSekce && (
                <SubMenu
                    section={otevrenaSekce}
                    currentPage={currentPage}
                    anchorTop={anchorTop}
                    onClose={zavriPanel}
                    onNavigate={(id) => { onNavigate(id); zavriPanel(); }}
                    onMouseEnter={zrusCasovac}
                    onMouseLeave={zavriPozdeji}
                    isDark={isDark}
                />
            )}

            {rezimSekci && (
                <SearchPalette
                    open={searchOpen}
                    onClose={() => setSearchOpen(false)}
                    user={user}
                    sekce={sekce}
                    onNavigate={onNavigate}
                    onUpdateSearchRecent={handleUpdateSearchRecent}
                    isDark={isDark}
                />
            )}

            <NotifikaceDropdown
                open={notifOpen}
                onClose={() => setNotifOpen(false)}
                onNavigate={(page, params) => { onNavigate(page, params); setNotifOpen(false); }}
                isDark={isDark}
                triggerRef={notifRef}
                panelStyle={{ bottom: 16, left: 68 }}
                user={user}
            />

            {profileOpen && (
                <div ref={profilePanelRef} style={{
                    position: 'fixed', bottom: 16, left: 68,
                    minWidth: 240, padding: 6,
                    ...MODAL_SKLO,
                    zIndex: 50,
                }}>
                    <div style={{ padding: '10px 12px 8px', borderBottom: `1px solid ${oddelovac}`, marginBottom: 4 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{user?.cele_jmeno}</p>
                        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0' }}>{user?.email}</p>
                    </div>

                    {/* Přihlášená zařízení — každý pro sebe. Společné Nastavení zaniklo
                        (revize 2026-10-12): firma → modul Správa, zbytek → ⚙ v modulech. */}
                    <button
                        onClick={() => { setProfileOpen(false); setZarizeniOpen(true); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm"
                        style={{ color: 'var(--text-primary)', background: 'none', border: 'none', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    >
                        <Laptop size={15} /> Přihlášená zařízení
                    </button>

                    <div style={{ padding: '4px 8px 6px', borderTop: `1px solid ${oddelovac}`, marginTop: 2 }}>
                        <p className="text-[10px] font-medium uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted)' }}>Vzhled</p>
                        <div style={{
                            display: 'flex', gap: 2,
                            background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                            borderRadius: 6, padding: 2,
                        }}>
                            {THEMES.map(t => (
                                <button key={t.v} onClick={() => setTheme(t.v)}
                                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs"
                                    style={{
                                        background: theme === t.v ? 'var(--brand-bg)' : 'transparent',
                                        color: theme === t.v ? 'var(--brand-text)' : 'var(--text-secondary)',
                                        border: 'none', cursor: 'pointer', transition: 'all 0.15s ease',
                                        fontWeight: theme === t.v ? 600 : 400,
                                    }}>
                                    <t.Icon size={12} />
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <button onClick={() => { setProfileOpen(false); onLogout(); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm"
                        style={{ color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer', marginTop: 2 }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <LogOut size={15} /> Odhlásit se
                    </button>
                </div>
            )}
            {zarizeniOpen && (
                <ModalShell onClose={() => setZarizeniOpen(false)} isDark={isDark} nadpis="Přihlášená zařízení" ikona={Laptop} barva="#6B7280" sirka={560}>
                    <div style={{ padding: 16 }}><PrihlasenaZarizeni isDark={isDark} /></div>
                </ModalShell>
            )}
        </>
    );
}
